import { useQuery } from '@apollo/client';
import { FilterList } from '@mui/icons-material';
import { Box, Button, Hidden } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { useAppContext } from '~/contexts';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { Filter, Table } from './components';
import { useFilterParams } from './lib';
import { NetworkAuditLogsDocument } from './queries/list.generated';
import { AuditLogActions } from './styles';

interface AuditLogListProps {
  channelId?: number;
  channelGuideId?: number;
  contentItemId?: number;
  dataSourceId?: number;
  deviceGroupId?: number;
  deviceSignalId?: number;
  deviceId?: number;
  eventTypeId?: number;
  fontId?: number;
  fullHeight?: boolean;
  layoutId?: number;
  mediaItemId?: number;
  playlistId?: number;
  recordTypes?: string[];
  roleId?: number;
  showId?: number;
  tvBrandId?: number;
  userId?: number;
}

export const AuditLogList = ({
  channelId,
  channelGuideId,
  contentItemId,
  dataSourceId,
  deviceGroupId,
  deviceSignalId,
  deviceId,
  eventTypeId,
  fontId,
  fullHeight = false,
  layoutId,
  mediaItemId,
  playlistId,
  recordTypes = [],
  roleId,
  showId,
  tvBrandId,
  userId,
}: AuditLogListProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { usersParams: auditUsers } = useFilterParams();
  const [showFilters, setShowFilters] = useState(auditUsers.length > 0);

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(NetworkAuditLogsDocument, {
    variables: {
      channelId,
      channelGuideId,
      contentItemId,
      dataSourceId,
      deviceId,
      deviceGroupId,
      deviceSignalId,
      eventTypeId,
      fontId,
      layoutId,
      mediaItemId,
      networkId: currentNetwork.id,
      page,
      perPage,
      playlistId,
      recordTypes,
      roleId,
      search,
      showId,
      tvBrandId,
      userId,
      userIds: auditUsers,
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.auditLogs.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.auditLogs.pageInfo.nodesCount);

  return (
    <Box sx={fullHeight ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
      <AuditLogActions style={fullHeight ? { flexShrink: 0 } : undefined}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SearchBar
            onChange={(value) =>
              setSearchParams((params) => {
                params.set('search', value);
                params.set('page', '1');
                return params;
              })
            }
            placeholder={t('components.audit_logs.misc.search_placeholder')}
            search={search}
          />
          <Hidden smDown>
            <Button
              onClick={() => setShowFilters(() => !showFilters)}
              startIcon={<FilterList />}
              variant="outlined"
            >
              {t('common.actions.filter')}
            </Button>
          </Hidden>
        </Box>
        <Count
          selectedCount={0}
          totalCount={totalCount || 0}
          thing="common.pluralize.audit_log_plural"
        />
      </AuditLogActions>
      {showFilters && (
        <Hidden smDown>
          <Filter />
        </Hidden>
      )}
      <Box sx={fullHeight ? { flex: 1, minHeight: 0 } : undefined}>
        <Table
          auditLogs={data?.network?.auditLogs.nodes ?? []}
          fullHeight={fullHeight}
          loading={loading}
          totalCount={totalCount}
        />
      </Box>
    </Box>
  );
};
